/**
 * TES Events Hook
 *
 * React hook for emitting kiosk events to TES.
 * Events are emitted asynchronously and errors are logged but don't break the UI flow.
 * Authentication is handled server-side via API key proxy.
 */

import { useCallback, useRef } from 'react';
import {
  emitItemCaptured,
  emitItemIdentified,
  emitReturnConfirmed,
  generateThingId,
  type DetectedItem,
  type KioskContext,
} from '../lib/tes-events';
import { Product } from '../context/AppContext';

interface UseTESEventsOptions {
  /** Kiosk/location identifier */
  kioskId?: string;
  /** Brand configuration ID */
  brandId?: string;
  /** Whether to enable event emission (default: true) */
  enabled?: boolean;
}

interface ThingMapping {
  productId: string;
  thingId: string;
  emittedAt: number;
}

export function useTESEvents(options: UseTESEventsOptions = {}) {
  const sessionIdRef = useRef<string>(generateSessionId());
  const thingMappingsRef = useRef<Map<string, ThingMapping>>(new Map());

  // Always enabled by default (auth handled server-side)
  const isEnabled = options.enabled ?? true;

  const getContext = useCallback((): KioskContext => ({
    kioskId: options.kioskId || 'pin-kiosk-default',
    sessionId: sessionIdRef.current,
    brandId: options.brandId,
  }), [options.kioskId, options.brandId]);

  /**
   * Emit captured + identified events for a detected product
   * Returns the thing ID for later reference
   */
  const emitProductDetected = useCallback(async (
    product: Product,
    detection?: {
      confidence?: number;
      method?: 'vision' | 'barcode' | 'manual';
    }
  ): Promise<string | null> => {
    if (!isEnabled) {
      console.log('[TES] Events disabled - skipping product detection event');
      return null;
    }

    const context = getContext();
    const thingId = generateThingId(context);

    // Store mapping for later reference
    thingMappingsRef.current.set(product.id, {
      productId: product.id,
      thingId,
      emittedAt: Date.now(),
    });

    const item: DetectedItem = {
      id: thingId,
      productRef: product.id,
      name: `${product.brand} ${product.name}`,
      type: product.category || 'product',
      brand: product.brand,
      confidence: detection?.confidence ?? product.detectionConfidence,
      quantity: 1,
      data: {
        size: product.size,
        price: product.price,
        image: product.image,
      },
    };

    try {
      // Emit captured event (creates the thing)
      console.log('[TES] Emitting item captured:', item.name);
      const capturedResult = await emitItemCaptured(item, context);

      if (capturedResult.success) {
        console.log('[TES] Item captured successfully:', capturedResult.eventId);

        // Emit identified event (adds status)
        console.log('[TES] Emitting item identified:', product.id);
        const identifiedResult = await emitItemIdentified(
          thingId,
          {
            productRef: product.id,
            name: product.name,
            confidence: detection?.confidence ?? product.detectionConfidence,
            matchMethod: detection?.method || 'vision',
          },
          context
        );

        if (identifiedResult.success) {
          console.log('[TES] Item identified successfully:', identifiedResult.eventId);
        } else {
          console.warn('[TES] Item identification failed:', identifiedResult.message);
        }
      } else {
        console.warn('[TES] Item capture failed:', capturedResult.message);
      }

      return thingId;
    } catch (error) {
      console.error('[TES] Error emitting product detection events:', error);
      return thingId; // Return ID anyway so flow can continue
    }
  }, [isEnabled, getContext]);

  /**
   * Emit captured + identified events for multiple products
   */
  const emitBatchProductsDetected = useCallback(async (
    products: Product[]
  ): Promise<Map<string, string>> => {
    const results = new Map<string, string>();

    if (!isEnabled) {
      console.log('[TES] Events disabled - skipping batch detection events');
      return results;
    }

    console.log(`[TES] Emitting batch detection for ${products.length} products`);

    // Process in parallel for speed
    await Promise.all(
      products.map(async (product) => {
        const thingId = await emitProductDetected(product);
        if (thingId) {
          results.set(product.id, thingId);
        }
      })
    );

    return results;
  }, [isEnabled, emitProductDetected]);

  /**
   * Emit return confirmed event for a product
   */
  const emitProductReturned = useCallback(async (
    product: Product,
    returnDetails?: {
      rewardType?: 'points' | 'credit' | 'discount' | 'donation';
      rewardValue?: number;
    }
  ): Promise<boolean> => {
    if (!isEnabled) {
      console.log('[TES] Events disabled - skipping return confirmed event');
      return true;
    }

    const context = getContext();
    const mapping = thingMappingsRef.current.get(product.id);

    // Use stored thing ID or generate new one
    const thingId = mapping?.thingId || generateThingId(context);

    try {
      console.log('[TES] Emitting return confirmed:', product.name);
      const result = await emitReturnConfirmed(
        thingId,
        {
          rewardType: returnDetails?.rewardType,
          rewardValue: returnDetails?.rewardValue,
          data: {
            product_id: product.id,
            product_name: `${product.brand} ${product.name}`,
          },
        },
        context
      );

      if (result.success) {
        console.log('[TES] Return confirmed successfully:', result.eventId);
        return true;
      } else {
        console.warn('[TES] Return confirmation failed:', result.message);
        return false;
      }
    } catch (error) {
      console.error('[TES] Error emitting return confirmed event:', error);
      return false;
    }
  }, [isEnabled, getContext]);

  /**
   * Emit return confirmed for multiple products
   */
  const emitBatchReturnsConfirmed = useCallback(async (
    products: Product[],
    returnDetails?: {
      rewardType?: 'points' | 'credit' | 'discount' | 'donation';
      rewardValue?: number;
    }
  ): Promise<boolean> => {
    if (!isEnabled) {
      console.log('[TES] Events disabled - skipping batch return events');
      return true;
    }

    console.log(`[TES] Emitting batch returns for ${products.length} products`);

    // Process in parallel
    const results = await Promise.all(
      products.map((product) => emitProductReturned(product, returnDetails))
    );

    const allSucceeded = results.every(Boolean);
    if (!allSucceeded) {
      console.warn('[TES] Some return confirmations failed');
    }

    return allSucceeded;
  }, [isEnabled, emitProductReturned]);

  /**
   * Get the thing ID for a product (if it was previously emitted)
   */
  const getThingId = useCallback((productId: string): string | null => {
    return thingMappingsRef.current.get(productId)?.thingId || null;
  }, []);

  /**
   * Reset session (generates new session ID and clears mappings)
   */
  const resetSession = useCallback(() => {
    sessionIdRef.current = generateSessionId();
    thingMappingsRef.current.clear();
    console.log('[TES] Session reset, new ID:', sessionIdRef.current);
  }, []);

  return {
    isEnabled,
    sessionId: sessionIdRef.current,
    emitProductDetected,
    emitBatchProductsDetected,
    emitProductReturned,
    emitBatchReturnsConfirmed,
    getThingId,
    resetSession,
  };
}

/**
 * Generate a unique session ID for this kiosk session
 */
function generateSessionId(): string {
  return `session_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 8)}`;
}

export default useTESEvents;
